import { Avatar, Box, type SxProps, type Theme } from '@mui/material';
import { BRAND } from '../app/theme';

export function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');
}

/** The person's square photo shown as a circle, or their initials when they have none. */
export function UserAvatar({
  name,
  avatarUrl,
  size = 36,
  decorative = false,
  ring = false,
  sx,
}: {
  name: string;
  avatarUrl?: string | null;
  size?: number;
  /** Next to the written name (chips, lists): hide from screen readers to avoid reading it twice. */
  decorative?: boolean;
  /** A brand ring, set slightly apart from the photo (the account menu). Adds to the size. */
  ring?: boolean;
  sx?: SxProps<Theme>;
}) {
  const avatar = (
    <Avatar
      src={avatarUrl ?? undefined}
      alt={decorative ? '' : name}
      aria-hidden={decorative || undefined}
      sx={[
        {
          width: size,
          height: size,
          bgcolor: 'primary.main',
          fontSize: size * 0.4,
          fontWeight: 600,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {initials(name)}
    </Avatar>
  );
  if (!ring) return avatar;

  // The ring is a masked gradient border, so the gap to the photo stays transparent on any
  // background. The white highlight turns slowly when pointed at.
  const width = Math.max(2, Math.round(size * 0.06));
  const outer = width + Math.max(2, Math.round(size * 0.07));
  return (
    <Box
      sx={{
        position: 'relative',
        display: 'inline-flex',
        flexShrink: 0,
        borderRadius: '50%',
        m: `${outer}px`,
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: -outer,
          borderRadius: '50%',
          p: `${width}px`,
          background: `conic-gradient(from 300deg, #FFFFFF, #A9CBEE 18%, ${BRAND.blue} 45%, #003B72 60%, ${BRAND.blue} 75%, #A9CBEE 90%, #FFFFFF)`,
          WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
          WebkitMaskComposite: 'xor',
          mask: 'linear-gradient(#000 0 0) content-box exclude, linear-gradient(#000 0 0)',
          transition: 'transform 600ms ease',
        },
        '&:hover::before': { transform: 'rotate(180deg)' },
        '@media (prefers-reduced-motion: reduce)': { '&::before': { transition: 'none' } },
      }}
    >
      {avatar}
    </Box>
  );
}
